import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Star, Crown } from 'lucide-react';
import VolunteerBadge from "./VolunteerBadge";

const PassportLeaderboard = ({ currentUserId }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("week");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/points/leaderboard?period=${period}&limit=5`)
      .then((r) => (r.ok ? r.json() : { leaderboard: [] }))
      .then((data) => { if (alive) setLeaderboard(data.leaderboard || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 3:
        return <Medal className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-[#1A1A1A]/40">{rank || "–"}</span>;
    }
  };

  const getRankBg = (rank, isCurrentUser) => {
    if (isCurrentUser) return 'bg-[#C85A38]/10 border-[#C85A38]/30';
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
      case 3:
        return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200';
      default:
        return 'bg-white border-stone-100';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm">
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-stone-100 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm" data-testid="passport-leaderboard">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C85A38] to-[#A84830] flex items-center justify-center">
          <Trophy className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-lg text-[#1A1A1A] leading-tight">{period === "week" ? "This week's podium" : "Hall of Fame"}</h3>
          <p className="text-xs text-[#1A1A1A]/60">{period === "week" ? "Resets Monday · MVP crowned Sunday night" : "All-time House Points"}</p>
        </div>
      </div>

        <div className="inline-flex rounded-full bg-[#F4ECE2] p-0.5 text-xs font-medium mb-3" role="tablist">
          {[["week", "This week"], ["all", "All time"]].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setPeriod(k)}
              className={`px-2.5 py-1 rounded-full transition-colors ${period === k ? "bg-white text-[#C85A38] shadow-sm" : "text-[#1A1A1A]/50"}`}
              data-testid={`leaderboard-period-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

      {leaderboard.length === 0 && (
        <p className="text-sm text-[#1A1A1A]/50 text-center py-4" data-testid="leaderboard-empty">
          {period === "week" ? "Nobody has earned a badge yet this week — the podium is wide open." : "No points yet."}
        </p>
      )}

      <div className="space-y-2">
        {leaderboard.map((entry) => {
          const isCurrentUser = entry.guest_id === currentUserId;
          return (
            <div
              key={entry.guest_id}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${getRankBg(entry.points > 0 ? entry.rank : 0, isCurrentUser)} ${isCurrentUser ? 'ring-2 ring-[#C85A38]/20' : ''}`}
              data-testid={`leaderboard-entry-${entry.rank}`}
            >
              {/* Rank */}
              <div className="flex-shrink-0 w-8 flex justify-center">
                {getRankIcon(entry.points > 0 ? entry.rank : 0)}
              </div>

              {/* Avatar */}
              <div className="flex-shrink-0">
                {entry.avatar ? (
                  <img
                    src={entry.avatar}
                    alt={entry.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#F0EDE3] flex items-center justify-center border-2 border-white shadow-sm">
                    <span className="text-sm font-medium text-[#1A1A1A]/60">
                      {entry.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}
              </div>

              {/* Name & Level */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`font-medium text-sm truncate ${isCurrentUser ? 'text-[#C85A38]' : 'text-[#1A1A1A]'}`}>
                    {entry.name}
                    {isCurrentUser && <span className="text-xs ml-1">(you)</span>}
                  </span>
                  <VolunteerBadge show={entry.is_volunteer} variant="dot" />
                </div>
                {entry.level && (
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-[#C85A38]" />
                    <span className="text-xs text-[#1A1A1A]/60">{entry.level}</span>
                  </div>
                )}
              </div>

              {/* Points */}
              <div className="text-right flex-shrink-0">
                <div className={`text-lg font-bold ${entry.rank === 1 && entry.points > 0 ? 'text-yellow-600' : 'text-[#C85A38]'}`}>
                  {entry.points}
                </div>
                <div className="text-xs text-[#1A1A1A]/50">pts</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* View full leaderboard link */}
      <div className="mt-4 text-center">
        <a
          href="/community?tab=ai"
          className="text-sm text-[#C85A38] hover:text-[#A84830] font-medium"
        >
          View your Logbook →
        </a>
      </div>
    </div>
  );
};

export default PassportLeaderboard;
